import {browserPlatform} from './browser';
import type {EmailCodeChallenge} from '../../src/email-sign-in-policy';

export type PendingEmailCode = Omit<EmailCodeChallenge, 'challengeToken'>;

/** Socket surface used by xterm and noVNC; credentials stay in the transport. */
export interface PlatformSocket {
  readonly url: string;
  readonly protocol: string;
  readonly extensions: string;
  readonly readyState: number;
  readonly bufferedAmount: number;
  binaryType: BinaryType;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
  addEventListener<K extends keyof WebSocketEventMap>(type: K, listener: (event: WebSocketEventMap[K]) => void): void;
  removeEventListener<K extends keyof WebSocketEventMap>(type: K, listener: (event: WebSocketEventMap[K]) => void): void;
}

export interface ClientPlatform {
  readonly kind: 'browser' | 'desktop';
  readonly publicOrigin: string;
  fetch(path: string, init?: RequestInit): Promise<Response>;
  webSocket(url: string): PlatformSocket;
  openPage(url: string): Promise<void>;
  openAccountPage(path: string): Promise<void>;
  loginWithPassword(email: string, password: string): Promise<void>;
  registerWithPassword(email: string, password: string): Promise<void>;
  pendingEmailCode(): Promise<PendingEmailCode | null>;
  startEmailCode(email: string): Promise<PendingEmailCode>;
  finishEmailCode(code: string): Promise<void>;
  cancelEmailCode(): Promise<void>;
  logout(): Promise<void>;
  accountChanged(accountId: string | null): void;
  saveFile(path: string, suggestedName?: string): Promise<void>;
}

let installed: ClientPlatform = browserPlatform;
export function platform(): ClientPlatform {return installed;}
/** The desktop entry installs this before importing the shared application. */
export function installPlatform(value: ClientPlatform): void {
  if (installed !== browserPlatform) throw new Error('The application platform is already installed.');
  const origin = new URL(value.publicOrigin);
  if (origin.protocol !== 'https:' || origin.origin !== value.publicOrigin || origin.username || origin.password) {
    throw new Error('The application server must have an HTTPS origin.');
  }
  installed = value;
}

export function reportPlatformError(error: unknown): void {
  window.dispatchEvent(new CustomEvent('eightbots-platform-error', {detail: error}));
}

/** Native sockets supply the underlying error; browser sockets can omit it. */
export function socketError(event: Event, connection: string): unknown {
  if (event instanceof ErrorEvent) {
    if (event.error !== undefined && event.error !== null) return event.error;
    if (event.message) return new Error(event.message);
  }
  return new Error(`The secure ${connection} connection failed. The browser did not supply further details.`);
}

export function accountApiPath(address: string): string | null {
  const value = new URL(address, platform().publicOrigin);
  if (value.origin !== platform().publicOrigin || value.username || value.password) return null;
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return value.pathname.startsWith(`${base}/api/`) ? `${value.pathname}${value.search}` : null;
}
